import { useState } from "react";
import { BookOpen, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const GUIDES = [
  {
    slug: "ndis-registration-checklist",
    title: "The Complete NDIS Registration Checklist",
    description: "A step by step roadmap covering documentation, evidence and audit readiness for new providers.",
    badge: "Most downloaded",
    pages: "32 pages",
  },
  {
    slug: "compliance-essentials",
    title: "NDIS Compliance Essentials Handbook",
    description: "An evergreen operational reference for staying compliant after registration is complete.",
    badge: "Updated 2025",
    pages: "48 pages",
  },
  {
    slug: "policy-toolkit",
    title: "NDIS Policy & Procedures Starter Toolkit",
    description: "A practical breakdown of the policy framework auditors expect to see and how to scope your own.",
    badge: "New",
    pages: "24 pages",
  },
];

export default function Resources() {
  const [modal, setModal] = useState({ open: false, guide: null });
  const openLead = (guide) => setModal({ open: true, guide });

  return (
    <div data-testid="resources-page">
      <section className="container-pad pt-16 pb-12">
        <div className="overline">Resources & Insights</div>
        <h1 className="font-display text-5xl sm:text-6xl text-navy mt-6 tracking-tight max-w-4xl leading-[1.05]">
          Practical knowledge written by consultants who run audits.
        </h1>
        <p className="text-muted2 mt-8 max-w-2xl leading-relaxed text-lg">
          Free guides, articles and tools to help you understand NDIS compliance whether you work with us or not.
        </p>
      </section>

      {/* GUIDES */}
      <section className="container-pad pb-24">
        <div className="overline">Downloadable guides</div>
        <h2 className="font-display text-3xl sm:text-4xl text-navy mt-4 tracking-tight">Free NDIS guides</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {GUIDES.map((g) => (
            <div key={g.slug} className="card-soft p-8 flex flex-col" data-testid={`guide-${g.slug}`}>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <BookOpen size={22} className="text-teal" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-teal bg-teal/10 px-3 py-1 rounded-full">{g.badge}</span>
              </div>
              <h3 className="font-display text-xl text-navy mt-6">{g.title}</h3>
              <p className="text-sm text-muted2 mt-3 leading-relaxed flex-1">{g.description}</p>
              <div className="text-xs text-muted2 mt-6 uppercase tracking-widest">{g.pages}</div>
              <button
                data-testid={`download-${g.slug}`}
                onClick={() => openLead(g)}
                className="btn-ochre mt-4 justify-center w-full"
              >
                Download <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad py-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-12 grain relative overflow-hidden">
          <div className="overline" style={{ color: '#A7E0E6' }}>Need tailored advice?</div>
          <h3 className="font-display text-3xl mt-3 max-w-2xl tracking-tight">Free 30 min discovery call with a senior consultant.</h3>
          <Link to="/contact" data-testid="resources-cta" className="btn-light-on-navy mt-8">
            Book my call <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <LeadCaptureModal open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })} guide={modal.guide} />
    </div>
  );
}
